import { uploadWorkspaceImage } from '../lib/rpc';
import { createEntityActions } from './entityActions';
import { createInteractiveCapability } from './interactive';
import type { AppContext } from '@ndrc/app-sdk/client';
import type { Clock } from '../lib/clock';
import type { MeResult, WorkspaceResult } from '../lib/queries';
import type { DeskManifest } from './registry';
import { createCallCapability } from './callSimulator';
import { reviewWork } from './reviewWork';

/**
 * L'`AppContext` **réellement** servi à une app.
 *
 * Chaque champ ici lit une donnée vraie : `me`, `desk.resolveWorkspace`, le manifeste, l'horloge
 * de Paris, la navigation du bureau. Il n'y a plus de troisième état : ce que le bureau ne sait
 * pas encore fournir n'est **pas dans le contrat** (`AppContext`, `@ndrc/app-sdk/client`), qui dit
 * en toutes lettres quel chantier le rebranchera. Lire un champ à venir ne lève donc plus au
 * premier rendu — cela ne compile plus du tout, ce qui est mieux : `pnpm typecheck` le voit, pas
 * la classe.
 *
 * Historique, pour qu'on ne le refasse pas : ces champs étaient servis par un `Proxy` dont le
 * piège `get` levait. React parcourt les objets qu'on lui confie (journal de rendu, DevTools,
 * `console.log`, `{...ctx}`) ; l'erreur partait donc toute seule et corrompait l'état interne de
 * React (« Should not already be working »). Un accès en lecture ne doit jamais lever.
 *
 * `permissions` mérite son explication : la V1 mince n'a pas encore de table de permissions
 * effectives par élève. Ce que le bureau sait, c'est (a) que l'app est activée pour la classe —
 * sans quoi elle ne serait pas montée — et (b) les permissions que son manifeste déclare utiliser.
 * `can()` répond donc sur cet ensemble-là, ce qui est vrai aujourd'hui et le restera : le jour où
 * une permission sera retirable élève par élève, cet ensemble rétrécira, il ne changera pas de
 * nature.
 */
export interface BuildAppContextInput {
  manifest: DeskManifest;
  enabledManifests?: readonly DeskManifest[];
  /** Permissions déclarées par le manifeste — le cœur ne les invente pas. */
  declaredPermissions: readonly string[];
  me: MeResult;
  klass: { id: string; name: string; isSandbox?: boolean } | null;
  workspace: WorkspaceResult | null;
  clock: Clock;
  navigate: (path: string) => void;
  openApp: (appId: string, path?: string) => void;
  openEnvironments: () => void;
  /**
   * L'élève dont l'enseignant lit le travail, ou `null` — c'est-à-dire `null` pour tout élève,
   * toujours. Ce que ce champ change, ci-dessous : `workspace.readOnly` devient enfin vrai, et les
   * deux capacités du cœur sont refermées sur CET élève au lieu de l'acteur.
   *
   * L'app, elle, ne reçoit rien de plus : `review()` reste une fonction sans paramètre, `call`
   * garde ses cinq fonctions sans identifiant (ADR-014, ADR-015). C'est l'hôte qui referme, comme
   * il referme déjà la classe — jamais l'app qui désigne.
   */
  observing: { classId: string; userId: string } | null;
}

export function buildAppContext(input: BuildAppContextInput): AppContext {
  if (!input.workspace && !input.klass)
    throw new Error('Sélectionnez une classe pour ouvrir cette activité.');
  const permissions: ReadonlySet<string> = new Set(input.declaredPermissions);

  return {
    app: { id: input.manifest.id, version: input.manifest.version },
    user: {
      id: input.me.id,
      role: input.me.role,
      displayName: input.me.displayName,
    },
    class: input.klass,
    group: null,
    scope: input.workspace
      ? { kind: 'workspace', workspaceId: input.workspace.workspaceId }
      : { kind: 'class', classId: input.klass!.id },
    /**
     * **Trois de ces champs étaient écrits en dur, et deux d'entre eux mentaient.** `kind`,
     * `isDemo` et `generation` viennent désormais de `desk.resolveWorkspace` (chantier 9) : un
     * enseignant en démonstration recevait `kind: 'student'` et `isDemo: false`, et une app qui
     * aurait cru ce contrat aurait écrit dans un espace en le prenant pour celui d'un élève.
     * `generation` valait `1` pour toujours, y compris après une remise à zéro.
     *
     * `readOnly` a rejoint les trois autres : il valait `false` en dur, ce qui était exact tant que
     * le bureau ne montait une app que sur un espace où l'acteur écrit. Depuis qu'un enseignant
     * peut LIRE le travail d'un élève dans les vrais outils, ce n'est plus vrai — et une app qui
     * aurait cru ce contrat aurait proposé « Enregistrer » sur un espace où le serveur refuse
     * toute écriture. Ce n'est pas ce champ qui protège (le refus vient du serveur : transaction
     * `read only`, opérations d'app refusées si elles ne sont pas des lectures) ; c'est lui qui
     * permet à une app de ne pas MENTIR à l'adulte avant même qu'il ne clique.
     *
     * `isSandbox` vient de la classe renvoyée par le serveur, jamais du nom de domaine.
     */
    workspace: input.workspace
      ? {
          id: input.workspace.workspaceId,
          kind: input.workspace.kind,
          readOnly: input.observing !== null || input.workspace.readOnly === true,
          isDemo: input.workspace.kind === 'demo',
          isSandbox: input.klass?.id === input.workspace.classId && input.klass.isSandbox === true,
          generation: input.workspace.generation,
        }
      : null,
    scopes: { class: null, activity: null },
    activity: null,
    ...(input.manifest.capabilities?.includes('interactive')
      ? {
          interactive: createInteractiveCapability(
            input.klass?.id,
            input.observing !== null || input.me.role !== 'student',
          ),
        }
      : {}),
    ...(input.workspace &&
    !input.observing &&
    !input.me.isAdmin &&
    !input.workspace.readOnly &&
    permissions.has('files.images.write')
      ? {
          images: {
            upload: (file: File) =>
              uploadWorkspaceImage(
                {
                  appId: input.manifest.id,
                  classId: input.workspace!.classId,
                  generation: input.workspace!.generation,
                  ...(input.workspace!.missionRunId
                    ? { missionRunId: input.workspace!.missionRunId }
                    : {}),
                },
                file,
              ),
          },
        }
      : {}),
    permissions,
    can: (permission) => permissions.has(permission),
    clock: { now: () => input.clock.now(), day: () => input.clock.day() },
    navigate: input.navigate,
    openApp: input.openApp,
    openEnvironments: input.openEnvironments,
    entityActions: createEntityActions(input.enabledManifests ?? [], input.openApp),
    // La PREMIÈRE des deux capacités du cœur ouvertes aux apps (ADR-014). Elle est refermée ICI sur la classe
    // de l'élève connecté : l'app reçoit une fonction SANS PARAMÈTRE, donc sans aucun endroit où
    // désigner une autre classe, un autre élève, un autre espace — ni une autre opération du
    // cœur. Voir `./reviewWork.ts`.
    review: () =>
      reviewWork(
        input.klass?.id,
        input.observing,
        input.workspace?.missionRunId,
        input.manifest.id,
      ),
    // La SECONDE capacité (ADR-015), refermée de la même façon : cinq fonctions qui n'acceptent
    // ni espace, ni élève, ni identifiant d'appel — voir `./callSimulator.ts`.
    call: createCallCapability(input.observing, input.workspace?.missionRunId, input.manifest.id),
  };
}
